// NodeFieldCoercion.js

import { normalizeEmailValueForStorage } from 'utilities/Email';
import {
  formatCurrencyDollarsString,
  getCurrencyCodeLabel,
  normalizeCurrencyCodeFieldValue,
  parseCurrencyInputToCents,
  resolveFieldCurrencyCode,
} from 'utilities/Currency';
import { isCurrencyCodeFieldType, isMoneyFieldType, isRelationFieldType } from 'utilities/FieldTypes';
import { normalizeBarcodeValue } from 'utilities/Barcode';
import { normalizeMeasurementValueForInput, normalizeMeasurementValueForStorage } from 'utilities/Measurement';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { normalizePhoneValueForStorage } from 'utilities/Phone';
import {
  buildUrlFieldValueFromInput,
  getUrlString,
  normalizeUrlFieldValue,
} from 'utilities/UrlFieldValue';
import { normalizeSignatureFieldValue } from 'utilities/SignatureFieldValue';
import { coerceToDateTimeRangeValue } from 'utilities/DateTime';


const normalizePercentage = (value) => {
  if (value === '' || value == null) return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  if (numeric < 0) return 0;
  if (numeric > 100) return 100;
  return Math.round(numeric);
};

function coerceBooleanFieldValue(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'yes', 'y', 'on'].includes(normalized)) return true;
    if (['false', 'no', 'n', 'off', ''].includes(normalized)) return false;
  }
  return false;
}

function coerceFromInput(value, type, field = null) {
  if (type === 'profile') return value ?? null; // preserve profile nodes
  if (type === 'richtext') return value ?? null;
  if (type === 'signature') return normalizeSignatureFieldValue(value);
  if (type === 'multiselect') {
    if (Array.isArray(value)) return value.filter((entry) => entry !== undefined && entry !== null);
    if (value == null || value === '') return [];
    return [value];
  }
  if (type === 'people') {
    if (Array.isArray(value)) return value;
    if (value == null) return [];
    return [value];
  }
  if (isRelationFieldType(type)) {
    return normalizeNodelistKeys(value);
  }
  if (type === 'tags') {
    if (Array.isArray(value)) return value.filter((entry) => entry !== undefined && entry !== null);
    if (value == null || value === '') return [];
    return [value];
  }
  if (type === 'datetimerange') {
    return coerceToDateTimeRangeValue(value);
  }
  if (type === 'annualdate' || type === 'date' || type === 'daterange' || type === 'datetime') {
    return value ?? null;
  }
  if (type === 'number') return value === '' ? '' : Number(value);
  if (type === 'percentage') return normalizePercentage(value);
  if (type === 'measurement') return normalizeMeasurementValueForStorage(value);
  if (isMoneyFieldType(type)) {
    const trimmed = String(value ?? '').trim();
    if (trimmed === '') return '';
    const parsed = parseCurrencyInputToCents(trimmed, resolveFieldCurrencyCode(field));
    return parsed.ok ? parsed.cents : '';
  }
  if (isCurrencyCodeFieldType(type) || isCurrencyCodeFieldType(field)) {
    return normalizeCurrencyCodeFieldValue(value);
  }
  if (type === 'barcode') {
    return normalizeBarcodeValue(value);
  }
  if (type === 'url') {
    if (value == null || value === '') return null;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return normalizeUrlFieldValue(value);
    }
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return null;
    return buildUrlFieldValueFromInput(trimmed, null);
  }
  if (type === 'email') return normalizeEmailValueForStorage(value);
  if (type === 'phone') return normalizePhoneValueForStorage(value);
  if (type === 'boolean') return coerceBooleanFieldValue(value);
  return value ?? '';
}

function coerceToInput(value, type, field = null) {
  if (type === 'profile') return value ?? null; // feed complex objects back untouched
  if (type === 'richtext') return value ?? null;
  if (type === 'signature') return normalizeSignatureFieldValue(value);
  if (type === 'multiselect') {
    if (Array.isArray(value)) return value;
    if (value == null || value === '') return [];
    return [value];
  }
  if (type === 'people') {
    if (Array.isArray(value)) return value;
    if (value == null || value === '') return [];
    return [value];
  }
  if (isRelationFieldType(type)) {
    return normalizeNodelistKeys(value);
  }
  if (type === 'tags') {
    if (Array.isArray(value)) return value;
    if (value == null || value === '') return [];
    return [value];
  }
  if (type === 'datetimerange') {
    return coerceToDateTimeRangeValue(value);
  }
  if (type === 'annualdate' || type === 'date' || type === 'daterange' || type === 'datetime') {
    return value ?? null;
  }
  if (type === 'boolean') return coerceBooleanFieldValue(value);
  if (type === 'measurement') return normalizeMeasurementValueForInput(value);
  if (isMoneyFieldType(type)) {
    return formatCurrencyDollarsString(value, resolveFieldCurrencyCode(field));
  }
  if (isCurrencyCodeFieldType(type) || isCurrencyCodeFieldType(field)) {
    const code = normalizeCurrencyCodeFieldValue(value);
    return code ? getCurrencyCodeLabel(code) : '';
  }
  if (type === 'percentage') return normalizePercentage(value);
  if (type === 'barcode') return normalizeBarcodeValue(value);
  if (type === 'email') return normalizeEmailValueForStorage(value);
  if (type === 'phone') return value == null ? '' : String(value);
  if (type === 'url') {
    if (value == null || value === '') return null;
    return normalizeUrlFieldValue(value) || buildUrlFieldValueFromInput(getUrlString(value), null);
  }
  if (value == null) return '';
  return value;
}

export {
  coerceBooleanFieldValue,
  coerceFromInput,
  coerceToInput,
};
